import { splitPatternClipData } from './patternSplit';

const defaultBounds = (clip) => ({
  startBeat: Number(clip?.startBeat) || 0,
  durationBeats: Math.max(0, Number(clip?.durationBeats) || 0),
});

export function deleteAudioRangeFromClips(
  clips,
  startBeat,
  endBeat,
  trackId = null,
  { includePatterns = false, getBounds = defaultBounds, idFactory = (index) => `range-${Date.now()}-${index}` } = {}
) {
  const rangeStart = Math.max(0, Math.min(Number(startBeat) || 0, Number(endBeat) || 0));
  const rangeEnd = Math.max(rangeStart, Math.max(Number(startBeat) || 0, Number(endBeat) || 0));
  if (rangeEnd - rangeStart <= 1e-6) {
    return { changed: false, clips, removedClipIds: [], newClips: [] };
  }

  const removedClipIds = [];
  const newClips = [];
  const nextClips = [];

  (clips || []).forEach((clip, index) => {
    const { startBeat: clipStart, durationBeats } = getBounds(clip);
    const clipEnd = clipStart + durationBeats;
    const affected = (clip.clipType === 'audio' || (includePatterns && clip.clipType === 'pattern'))
      && (!trackId || (Array.isArray(trackId) ? trackId.includes(clip.trackId) : clip.trackId === trackId))
      && rangeStart < clipEnd
      && rangeEnd > clipStart;
    if (!affected) {
      nextClips.push(clip);
      return;
    }

    removedClipIds.push(clip.id);
    const cutStart = Math.max(clipStart, rangeStart);
    const cutEnd = Math.min(clipEnd, rangeEnd);
    const sourceId = clip.sourceClipId || clip.id;
    const idBase = idFactory(index, clip);
    let leftData = clip.clipData, rightData = clip.clipData;
    if (clip.clipType === 'pattern' && clip.clipData) {
      const ppq = Number(clip.clipData.ppq) || 96;
      const options = { drumPattern: clip.clipData.kind !== 'instrument' };
      // The splitter clamps interior cuts to at least one tick. Do not use
      // it for a boundary at zero: that would shift surviving notes by a tick.
      let remainder = clip.clipData;
      if (cutStart > clipStart + 1e-6) {
        const first = splitPatternClipData(clip.clipData, (cutStart-clipStart)*ppq, durationBeats*ppq, options);
        leftData = first.left;
        remainder = first.right;
      }
      if (cutEnd < clipEnd - 1e-6) {
        rightData = splitPatternClipData(remainder, (cutEnd-cutStart)*ppq, (clipEnd-cutStart)*ppq, options).right;
      }
    }

    if (cutStart > clipStart + 1e-6) {
      const left = {
        ...clip,
        id: `${idBase}-L`,
        ...(clip.clipType === 'pattern' ? {clipData:leftData} : {}),
        startBeat: clipStart,
        durationBeats: cutStart - clipStart,
        sourceClipId: sourceId,
        timelineStartSample: null,
        lengthSamples: null,
        sourceStartSample: null,
      };
      nextClips.push(left);
      newClips.push(left);
    }
    if (cutEnd < clipEnd - 1e-6) {
      const right = {
        ...clip,
        id: `${idBase}-R`,
        ...(clip.clipType === 'pattern' ? {clipData:rightData} : {}),
        startBeat: cutEnd,
        durationBeats: clipEnd - cutEnd,
        ...(clip.clipType === 'audio' ? { audioOffset: (clip.audioOffset || 0) + (cutEnd - clipStart) } : {}),
        sourceClipId: sourceId,
        timelineStartSample: null,
        lengthSamples: null,
        sourceStartSample: null,
      };
      nextClips.push(right);
      newClips.push(right);
    }
  });

  return {
    changed: removedClipIds.length > 0,
    clips: nextClips,
    removedClipIds,
    newClips,
  };
}
